import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Music, Calendar, Sparkles, Award, Trophy, Zap, Crown, CalendarHeart,
  Medal, Gem, Mic, Drum, Rocket, ChevronRight, Lock,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Compact achievements card for the Dashboard.
 * Shows: earned/total ratio, the 3 most recent unlocked badges, and a
 * "Next milestone" pointer to motivate the player.
 *
 * Added in iter-66d. Iter-106: celebrate newly earned badges via toast
 * and surface a pulse on freshly earned chips. Iter-126f: added
 * Mic / Drum / Rocket icons for Concert Debut, Rehearsal Stalwart and
 * Quick Draw badges.
 */

const ICON_MAP = {
  Music, Calendar, Sparkles, Award, Trophy, Zap, Crown, CalendarHeart,
  Medal, Gem, Mic, Drum, Rocket,
};

const TIER_COLOR = {
  bronze: "text-amber-600",
  silver: "text-slate-300",
  gold: "text-primary",
  platinum: "text-purple-300",
};

const AchievementsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/me/achievements")
      .then((res) => {
        setData(res.data);
        // iter-106 — celebrate any badges the user hasn't seen yet.
        const unseen = (res.data?.badges || []).filter((b) => b.unseen);
        if (unseen.length > 0) {
          unseen.slice(0, 3).forEach((b, i) => {
            setTimeout(() => {
              toast.success(`🎉 New badge unlocked: ${b.name}`, {
                description: b.description,
                duration: 6000,
              });
            }, i * 400);
          });
          // Acknowledge so we don't celebrate again on next load.
          apiClient.post("/me/achievements/acknowledge").catch(() => {});
        }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  const earned = (data.badges || []).filter((b) => b.earned);
  const next = (data.badges || []).find((b) => !b.earned);

  // Hide the card entirely for brand-new users with zero attendance to avoid clutter
  if (data.total_events_attended === 0 && earned.length === 0) return null;

  return (
    <Card className="glass-card" data-testid="dashboard-achievements-card">
      <CardHeader className="border-b border-white/5 pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            Achievements
          </CardTitle>
          <Link
            to="/achievements"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="view-all-achievements-link"
          >
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-heading font-bold text-primary tabular-nums">
            {data.earned_count}
          </span>
          <span className="text-sm text-muted-foreground">/ {data.total_count} badges earned</span>
        </div>

        {earned.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="recently-earned-badges">
            {earned.slice(0, 4).map((b) => {
              const Icon = ICON_MAP[b.icon] || Trophy;
              const color = TIER_COLOR[b.tier] || "text-primary";
              return (
                <div
                  key={b.id}
                  title={`${b.name} — ${b.description}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/50 border border-white/5 ${b.unseen ? "ring-2 ring-primary/60 animate-pulse" : ""}`}
                  data-testid={`dashboard-badge-${b.id}`}
                >
                  <Icon className={`w-3.5 h-3.5 ${color}`} />
                  <span className="text-xs font-medium">{b.name}</span>
                  {b.unseen && <span className="text-[10px] text-primary font-semibold">NEW</span>}
                </div>
              );
            })}
            {earned.length > 4 && (
              <Link
                to="/achievements"
                className="text-xs text-muted-foreground self-center hover:text-foreground"
              >
                +{earned.length - 4} more
              </Link>
            )}
          </div>
        )}

        {next && (
          <div className="pt-3 border-t border-white/5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-secondary/40 flex items-center justify-center flex-shrink-0">
                <Lock className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate">{next.name}</p>
                  <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                    {next.progress} / {next.target}
                  </span>
                </div>
                <div className="h-1 w-full rounded-full bg-secondary mt-2 overflow-hidden">
                  <div
                    className="h-full bg-primary/70 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (next.progress / next.target) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AchievementsCard;
